import { useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Cpu,
  Database,
  ExternalLink,
  Package,
  Settings,
  ShieldCheck,
  Store,
  Zap,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { BatchRunFeatureFlags } from "./BatchRunFeatureFlags";
import type { DedupeMode, RulePackSource } from "./BatchRunSummary";

interface RulePack {
  id: string;
  name: string;
  version: string;
  status: string;
}

interface MerchantInfo {
  merchant_key: string;
  display_name: string | null;
}

interface BatchRunSettingsPanelProps {
  run: {
    sources_enabled: string[];
    config_json: Record<string, unknown>;
    normalize_rule_pack_id: string | null;
    normalize_rule_pack_source: RulePackSource | null;
    dedupe_rule_pack_id: string | null;
    dedupe_rule_pack_source: RulePackSource | null;
    dedupe_mode: DedupeMode | null;
    dedupe_ai_enabled: boolean | null;
    dedupe_ai_provider: string | null;
    dedupe_ai_confidence_threshold: number | null;
  };
}

const SOURCE_BADGES: Record<RulePackSource, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  run_override: { label: "Run Override", variant: "secondary" },
  resolved: { label: "Resolved", variant: "default" },
  missing: { label: "Missing", variant: "destructive" },
  embedded_default: { label: "Embedded Default", variant: "destructive" },
};

const MODE_LABELS: Record<DedupeMode, { label: string; icon: React.ReactNode; variant: "default" | "secondary" | "outline" }> = {
  deterministic: { label: "Deterministic", icon: <Zap className="h-3 w-3" />, variant: "outline" },
  hybrid_ai_assist: { label: "Hybrid (AI Assist)", icon: <Cpu className="h-3 w-3" />, variant: "secondary" },
  ai_only: { label: "AI Only", icon: <Cpu className="h-3 w-3" />, variant: "default" },
};

const MERCHANTS_COLLAPSED_LIMIT = 8;

export function BatchRunSettingsPanel({ run }: BatchRunSettingsPanelProps) {
  const navigate = useNavigate();
  const [normalizePack, setNormalizePack] = useState<RulePack | null>(null);
  const [dedupePack, setDedupePack] = useState<RulePack | null>(null);
  const [merchantsExpanded, setMerchantsExpanded] = useState(false);
  const [merchantInfos, setMerchantInfos] = useState<MerchantInfo[]>([]);

  const merchantKeys = useMemo(() => {
    const config = run.config_json || {};
    return ((config.merchants as string[]) || (config.merchant_ids as string[]) || []);
  }, [run.config_json]);

  useEffect(() => {
    fetchPacks();
  }, [run.normalize_rule_pack_id, run.dedupe_rule_pack_id]);

  useEffect(() => {
    if (merchantKeys.length > 0) {
      fetchMerchantInfos();
      return;
    }
    setMerchantInfos([]);
  }, [merchantKeys]);

  const fetchPacks = async () => {
    try {
      if (run.normalize_rule_pack_id) {
        const { data } = await supabase
          .from("normalize_rule_packs")
          .select("id, name, version, status")
          .eq("id", run.normalize_rule_pack_id)
          .single();
        setNormalizePack(data as RulePack | null);
      } else {
        setNormalizePack(null);
      }

      if (run.dedupe_rule_pack_id) {
        const { data } = await supabase
          .from("dedupe_rule_packs")
          .select("id, name, version, status")
          .eq("id", run.dedupe_rule_pack_id)
          .single();
        setDedupePack(data as RulePack | null);
      } else {
        setDedupePack(null);
      }
    } catch (err) {
      console.error("[BatchRunSettingsPanel] Failed to fetch packs:", err);
    }
  };

  const fetchMerchantInfos = async () => {
    try {
      const { data, error } = await supabase
        .from("merchants")
        .select("merchant_key, display_name")
        .in("merchant_key", merchantKeys);

      if (error) throw error;

      const merchantMap = new Map((data || []).map((m) => [m.merchant_key, m.display_name]));
      const infos: MerchantInfo[] = merchantKeys.map((key) => ({
        merchant_key: key,
        display_name: merchantMap.get(key) || null,
      }));
      setMerchantInfos(infos);
    } catch (err) {
      console.error("[BatchRunSettingsPanel] Failed to fetch merchant infos:", err);
      setMerchantInfos(merchantKeys.map((key) => ({ merchant_key: key, display_name: null })));
    }
  };

  const hasEmbeddedDefault = run.normalize_rule_pack_source === "embedded_default" || run.dedupe_rule_pack_source === "embedded_default";
  const modeInfo = run.dedupe_mode ? MODE_LABELS[run.dedupe_mode] : null;

  const renderPackValue = (
    pack: RulePack | null,
    packId: string | null,
    source: RulePackSource | null,
    packType: "Normalize" | "Dedup",
  ) => {
    const sourceInfo = source ? SOURCE_BADGES[source] : null;

    if (source === "embedded_default") {
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground italic">Embedded Default</span>
          <Badge variant="destructive" className="gap-1 text-xs">
            <AlertTriangle className="h-3 w-3" />
            Fallback
          </Badge>
        </div>
      );
    }

    if (!pack && packId) {
      return <span className="text-sm text-muted-foreground">Loading...</span>;
    }

    if (!pack) {
      return <span className="text-sm text-muted-foreground italic">Not set (resolves at step execution)</span>;
    }

    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm">{pack.name} · v{pack.version}</span>
        {sourceInfo && (
          <Badge variant={sourceInfo.variant} className="text-xs">{sourceInfo.label}</Badge>
        )}
        {packType === "Dedup" && modeInfo && (
          <Badge variant={modeInfo.variant} className="gap-1 text-xs">
            {modeInfo.icon}
            {modeInfo.label}
          </Badge>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Settings className="h-4 w-4" />
        <h2 className="text-sm font-semibold">Batch Run Settings</h2>
      </div>

      {hasEmbeddedDefault && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No active rule pack was configured. Embedded defaults were used.
            <Button variant="link" size="sm" onClick={() => navigate("/admin/command-center")} className="p-0 h-auto ml-1">
              Set an active pack in Command Center
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-3 rounded-md border p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Rule Packs</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/command-center")} className="text-xs h-7">
            View in Command Center
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
        <div className="pl-6 space-y-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">Normalize:</span>
            {renderPackValue(normalizePack, run.normalize_rule_pack_id, run.normalize_rule_pack_source, "Normalize")}
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">Dedup:</span>
            {renderPackValue(dedupePack, run.dedupe_rule_pack_id, run.dedupe_rule_pack_source, "Dedup")}
          </div>
        </div>
        {run.dedupe_ai_enabled && (
          <div className="pl-6 p-2 bg-muted/50 rounded-md">
            <div className="flex items-center gap-2 text-sm">
              <Cpu className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">AI Provider:</span>
              <span>{run.dedupe_ai_provider}</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">Threshold:</span>
              <span>{((run.dedupe_ai_confidence_threshold || 0.85) * 100).toFixed(0)}%</span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Sources Enabled</span>
        </div>
        <div className="pl-6 flex flex-wrap gap-1.5">
          {run.sources_enabled.length > 0 ? (
            run.sources_enabled.map((source) => (
              <Badge key={source} variant="outline">{source}</Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground italic">None configured</span>
          )}
        </div>
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center gap-2">
          <Store className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Merchants</span>
          <Badge variant="secondary" className="text-xs">{merchantInfos.length}</Badge>
        </div>
        <div className="pl-6 space-y-1">
          {merchantInfos.length === 0 ? (
            <span className="text-sm text-muted-foreground italic">No merchants configured</span>
          ) : (
            <>
              {(merchantsExpanded ? merchantInfos : merchantInfos.slice(0, MERCHANTS_COLLAPSED_LIMIT)).map((merchant) => (
                <div key={merchant.merchant_key} className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{merchant.display_name || merchant.merchant_key}</span>
                  {merchant.display_name && (
                    <span className="text-muted-foreground text-xs">({merchant.merchant_key})</span>
                  )}
                </div>
              ))}
              {merchantInfos.length > MERCHANTS_COLLAPSED_LIMIT && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMerchantsExpanded(!merchantsExpanded)}
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  {merchantsExpanded ? (
                    <><ChevronUp className="h-3 w-3 mr-1" />Show less</>
                  ) : (
                    <><ChevronDown className="h-3 w-3 mr-1" />+{merchantInfos.length - MERCHANTS_COLLAPSED_LIMIT} more</>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Advanced</span>
        </div>
        <div className="pl-6">
          <BatchRunFeatureFlags />
        </div>
      </div>
    </div>
  );
}
